
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Upload, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { StudyNote } from "@/types/notes";

interface StudyNoteUploaderProps {
  isOpen: boolean;
  onClose: () => void;
  onNoteCreated: (note: StudyNote) => void;
}

export const StudyNoteUploader: React.FC<StudyNoteUploaderProps> = ({ 
  isOpen,
  onClose,
  onNoteCreated
}) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      // Limit file size to 5MB
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size exceeds 5MB limit");
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please provide a title for your note");
      return;
    }

    if (!subject.trim()) {
      toast.error("Please provide a subject for your note");
      return;
    }

    if (!category.trim()) {
      toast.error("Please provide a category for your note");
      return;
    }

    setIsUploading(true);
    try {
      let fileUrl = "";
      
      // Upload file if selected
      if (file) {
        const uniqueFileName = `${Date.now()}_${file.name}`;
        const filePath = `${category}/${subject}/${uniqueFileName}`;
        
        // Upload file to Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('study_notes')
          .upload(filePath, file);

        if (uploadError) {
          throw new Error(`File upload error: ${uploadError.message}`);
        }
        
        // Get public URL
        const { data } = supabase.storage
          .from('study_notes')
          .getPublicUrl(filePath);
          
        fileUrl = data.publicUrl;
      }

      // Create a new note
      const { data: noteData, error: noteError } = await supabase
        .from('study_notes')
        .insert({
          title,
          content,
          subject,
          category,
          file_url: fileUrl,
          content_type: "text"
        })
        .select();

      if (noteError) {
        throw noteError;
      }

      // Create a properly formatted note object
      const newNote: StudyNote = {
        id: noteData[0].id,
        title: noteData[0].title,
        content: noteData[0].content,
        subject: noteData[0].subject,
        category: noteData[0].category,
        fileUrl: noteData[0].file_url,
        contentType: "text",
        createdAt: noteData[0].created_at,
        updatedAt: noteData[0].updated_at
      };

      onNoteCreated(newNote);
      toast.success("Study note created successfully!");
      setTitle("");
      setContent("");
      setSubject("");
      setCategory("");
      setFile(null);
      onClose();
      
    } catch (error) {
      console.error("Error creating study note:", error);
      toast.error("Failed to create study note. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Create Study Note</DialogTitle>
          <DialogDescription>
            Add a new study note to your collection
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="title" className="text-sm font-medium">
                Title
              </label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter note title"
                required
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="subject" className="text-sm font-medium">
                Subject
              </label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject (e.g., Math, Science)"
                required
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="category" className="text-sm font-medium">
                Category
              </label>
              <Input
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Enter category (e.g., Algebra, Physics)"
                required
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="content" className="text-sm font-medium">
                Content
              </label>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter note content"
                rows={5}
                required
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="file" className="text-sm font-medium">
                Attachment (Optional)
              </label>
              <Input
                id="file"
                type="file"
                onChange={handleFileChange}
                className="flex-1"
                accept=".pdf,.doc,.docx,.txt,.md,.ppt,.pptx"
              />
              <p className="text-xs text-gray-500">
                Max file size: 5MB. Supported formats: PDF, DOC, DOCX, TXT, MD, PPT, PPTX
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Create Note
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StudyNoteUploader;
